import { parseJSONData, propertyInterestDataSchema, type Enums } from '@aikoa/common'
import { useTranslations, type TFunction } from '@aikoa/i18n/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useLocalSearchParams, useRouter } from 'expo-router'
import React, { useState } from 'react'
import { Pressable, ScrollView, View } from 'react-native'
import { useAlert } from '~/components/GlobalAlert'
import Icon from '~/components/Icon'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { SectionTitle } from '~/components/layout/Sections'
import { Body, Label } from '~/components/typography/Content'
import { Heading2, Heading3 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger } from '~/components/ui/select'
import { Text } from '~/components/ui/text'
import { useUserId } from '~/components/user/SessionProvider'
import designTokens from '~/lib/designTokens'
import { queryClient, trpc, trpcClient } from '~/lib/trpc'

type UserRole = Enums<'user_role'>

function getRoleLabel(role: UserRole, t: TFunction<'admin'>): string {
  const roleMap: Record<UserRole, string> = {
    realtor: t(($) => $.users.roles.realtor),
    buyer: t(($) => $.users.roles.buyer),
    seller: t(($) => $.users.roles.seller),
  }
  return roleMap[role]
}

type ObjectType = 'asset' | 'contact' | 'contactRequest' | 'feedback' | 'event' | 'interest'

type FeedbackStatus = 'pending' | 'responded' | 'rejected'

function FeedbackStatusBadge({ status }: { status: FeedbackStatus }) {
  const config: Record<FeedbackStatus, { label: string; className: string }> = {
    pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700' },
    responded: { label: 'Responded', className: 'bg-green-100 text-green-700' },
    rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
  }
  const { label, className } = config[status]

  return (
    <View className={`rounded-full px-2 py-0.5 ${className}`}>
      <Text className="font-medium text-xs">{label}</Text>
    </View>
  )
}

function SectionHeader({
  title,
  count,
  isOpen,
}: {
  title: string
  count: number
  isOpen: boolean
}) {
  return (
    <View className="flex-row items-center justify-between">
      <View className="flex-row items-center gap-2">
        <Heading3>{title}</Heading3>
        <View className="rounded-full bg-neutral-200 px-2 py-0.5">
          <Text className="font-medium text-xs text-neutral-600">{count}</Text>
        </View>
      </View>
      <Icon
        name={isOpen ? 'chevron-up-outline' : 'chevron-down-outline'}
        size={20}
        color={designTokens.foreground}
      />
    </View>
  )
}

type SectionKey =
  | 'assets'
  | 'contacts'
  | 'contactRequests'
  | 'feedback'
  | 'events'
  | 'interests'
  | 'feedbackAsResponder'
  | 'matchingContacts'

function ObjectRow({
  label,
  sublabel,
  badge,
  onPress,
  onDelete,
  isDeleting,
}: {
  label: string
  sublabel?: string
  badge?: React.ReactNode
  onPress?: () => void
  onDelete: () => void
  isDeleting: boolean
}) {
  return (
    <Pressable
      onPress={onPress}
      className="flex-row items-center justify-between gap-3 border-b border-neutral-100 py-3 last:border-b-0 active:bg-neutral-50">
      <View className="flex-1 gap-1">
        <View className="flex-row items-center gap-2">
          <Text className="text-sm">{label}</Text>
          {badge}
        </View>
        {sublabel && <Label className="text-xs text-secondary">{sublabel}</Label>}
      </View>
      <Pressable
        onPress={(e) => {
          e.stopPropagation()
          onDelete()
        }}
        disabled={isDeleting}
        className="rounded-md bg-red-100 p-2 active:bg-red-200">
        <Icon name="trash-outline" size={16} color={isDeleting ? '#ccc' : '#dc2626'} />
      </Pressable>
    </Pressable>
  )
}

function ReadOnlyRow({
  label,
  sublabel,
  badge,
  onPress,
}: {
  label: string
  sublabel?: string
  badge?: React.ReactNode
  onPress?: () => void
}) {
  return (
    <Pressable
      onPress={onPress}
      className="border-b border-neutral-100 py-3 last:border-b-0 active:bg-neutral-50">
      <View className="flex-row items-center gap-2">
        <Text className="text-sm">{label}</Text>
        {badge}
      </View>
      {sublabel && <Label className="text-xs text-secondary">{sublabel}</Label>}
    </Pressable>
  )
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <View className="gap-1 py-2">
      <Label className="text-xs text-secondary">{label}</Label>
      <Text className="text-sm">{value}</Text>
    </View>
  )
}

type ObjectDetail = {
  title: string
  fields: { label: string; value?: string | null }[]
}

export default function UserDetails() {
  const { t: tAdmin } = useTranslations('admin')
  const { t: tCommon } = useTranslations('common')

  const { userId } = useLocalSearchParams<{ userId: string }>()
  const router = useRouter()
  const { showAlert } = useAlert()
  const currentUserId = useUserId()
  const isSelf = currentUserId === userId

  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    assets: false,
    contacts: false,
    contactRequests: false,
    feedback: false,
    events: false,
    interests: false,
    feedbackAsResponder: false,
    matchingContacts: false,
  })

  const [selectedObject, setSelectedObject] = useState<ObjectDetail | null>(null)
  const [editingSlug, setEditingSlug] = useState(false)
  const [slugInput, setSlugInput] = useState('')

  const toggleSection = (key: SectionKey) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const queryKey = trpc.admin.getAdminUserDetails.queryKey({ userId })

  const { data, isLoading, error } = useQuery(
    trpc.admin.getAdminUserDetails.queryOptions({ userId })
  )

  const deleteObjectMutation = useMutation({
    mutationFn: (input: { objectType: ObjectType; objectId: string }) =>
      trpcClient.admin.adminDeleteObject.mutate(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
    },
    onError: () => {
      showAlert({
        type: 'error',
        title: tCommon(($) => $.error.error),
        description: tAdmin(($) => $.users.details.deleteObjectError),
      })
    },
  })

  const deleteUserMutation = useMutation({
    mutationFn: () => trpcClient.admin.adminDeleteUser.mutate({ userId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: trpc.admin.listUsersForAdmin.queryKey(),
      })
      router.back()
    },
    onError: () => {
      showAlert({
        type: 'error',
        title: tCommon(($) => $.error.error),
        description: tAdmin(($) => $.users.details.delete.error),
      })
    },
  })

  const setRoleMutation = useMutation(
    trpc.users.setExclusiveRole.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey })
        void queryClient.invalidateQueries({
          queryKey: trpc.admin.listUsersForAdmin.queryKey(),
        })
      },
      onError: () => {
        showAlert({
          type: 'error',
          title: tCommon(($) => $.error.error),
          description: tAdmin(($) => $.users.details.roleChangeError),
        })
      },
    })
  )

  const updateSlugMutation = useMutation({
    mutationFn: (slug: string) => trpcClient.admin.updateUserSlug.mutate({ userId, slug }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
      void queryClient.invalidateQueries({
        queryKey: trpc.admin.listUsersForAdmin.queryKey(),
      })
      setEditingSlug(false)
    },
    onError: () => {
      showAlert({
        type: 'error',
        title: tCommon(($) => $.error.error),
        description: tAdmin(($) => $.users.details.editSlug.error),
      })
    },
  })

  const handleRoleChange = (option: { value: string; label: string } | undefined) => {
    if (option?.value && option.value !== data?.user.role) {
      setRoleMutation.mutate({ userId, role: option.value as UserRole })
    }
  }

  const confirmDelete = (objectType: ObjectType, objectId: string, name: string) => {
    showAlert({
      type: 'confirm',
      title: tAdmin(($) => $.users.details.deleteConfirm.title),
      description: tAdmin(($) => $.users.details.deleteConfirm.description, {
        name,
      }),
      actions: [
        {
          label: tCommon(($) => $.actions.delete),
          buttonVariant: 'destructive',
          onPress: () => deleteObjectMutation.mutate({ objectType, objectId }),
        },
      ],
    })
  }

  const confirmDeleteUser = () => {
    showAlert({
      type: 'confirm',
      title: tAdmin(($) => $.users.details.delete.confirmTitle),
      description: tAdmin(($) => $.users.details.delete.confirmDescription),
      actions: [
        {
          label: tAdmin(($) => $.users.details.delete.button),
          buttonVariant: 'destructive',
          onPress: () => deleteUserMutation.mutate(),
        },
      ],
    })
  }

  if (isLoading) {
    return (
      <ScreenView>
        <ContentBox className="flex-1" variant="transparent">
          <Body className="text-center text-secondary">
            {tAdmin(($) => $.users.details.loading)}
          </Body>
        </ContentBox>
      </ScreenView>
    )
  }

  if (error || !data) {
    return (
      <ScreenView>
        <ContentBox className="flex-1" variant="transparent">
          <Body className="text-center text-red-600">
            {tAdmin(($) => $.users.details.loadError)}
          </Body>
        </ContentBox>
      </ScreenView>
    )
  }

  const {
    user,
    assets,
    contacts,
    contactRequests,
    feedback,
    events,
    interests,
    feedbackAsResponder,
    matchingContacts,
  } = data

  return (
    <ScreenView>
      <ContentBox className="flex-1" contentContainerClassName="gap-6" variant="transparent">
        {/* User header */}
        <View className="gap-3 rounded-xl bg-white p-4">
          <View className="flex-row items-center justify-between gap-4">
            <Heading2 className="flex-1">{user.name}</Heading2>
            <Select
              value={{
                value: user.role,
                label: getRoleLabel(user.role as UserRole, tAdmin),
              }}
              onValueChange={handleRoleChange}
              disabled={setRoleMutation.isPending}>
              <SelectTrigger className="h-10 w-36 border-neutral-300">
                <Text className="text-sm">{getRoleLabel(user.role as UserRole, tAdmin)}</Text>
              </SelectTrigger>
              <SelectContent align="end" sideOffset={4}>
                <SelectItem value="realtor" label={getRoleLabel('realtor', tAdmin)} />
                <SelectItem value="buyer" label={getRoleLabel('buyer', tAdmin)} />
              </SelectContent>
            </Select>
          </View>
          {!!user.phoneNumber && <Label className="text-neutral-600">{user.phoneNumber}</Label>}
          {!!user.email && <Label className="text-neutral-600">{user.email}</Label>}
          <Label className="text-xs text-neutral-400">{user.userId}</Label>

          <View className="flex-row items-center justify-between gap-2">
            <View className="gap-0.5">
              <Label className="text-xs text-secondary">
                {tAdmin(($) => $.users.details.fields.slug)}
              </Label>
              <Text className="text-sm text-neutral-500">{user.slug ?? '—'}</Text>
            </View>
            <Pressable
              onPress={() => {
                setSlugInput(user.slug ?? '')
                setEditingSlug(true)
              }}
              className="rounded-md bg-neutral-100 p-2 active:bg-neutral-200">
              <Icon name="pencil-outline" size={16} color={designTokens.foreground} />
            </Pressable>
          </View>
        </View>

        {/* Delete user button */}
        <Button
          variant="destructive"
          onPress={confirmDeleteUser}
          loading={deleteUserMutation.isPending}
          disabled={isSelf}>
          <ButtonLabel>
            {isSelf
              ? tAdmin(($) => $.users.details.delete.cannotDeleteSelf)
              : tAdmin(($) => $.users.details.delete.button)}
          </ButtonLabel>
        </Button>
        {/* Assets section */}
        <Collapsible
          className="rounded-xl bg-white"
          isOpen={openSections.assets}
          onOpenChange={() => toggleSection('assets')}>
          <CollapsibleTrigger className="w-full p-4">
            <SectionHeader
              title={tAdmin(($) => $.users.details.sections.assets)}
              count={assets.length}
              isOpen={openSections.assets}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4">
            {assets.length === 0 ? (
              <Body className="text-sm text-secondary">
                {tAdmin(($) => $.users.details.emptyStates.assets)}
              </Body>
            ) : (
              assets.map((asset) => (
                <ObjectRow
                  key={asset.relationId}
                  label={asset.name}
                  sublabel={asset.relationType}
                  onPress={() =>
                    setSelectedObject({
                      title: asset.name,
                      fields: [
                        {
                          label: tAdmin(($) => $.users.details.fields.id),
                          value: asset.assetId,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.relation),
                          value: asset.relationType,
                        },
                      ],
                    })
                  }
                  onDelete={() => confirmDelete('asset', asset.relationId, asset.name)}
                  isDeleting={
                    deleteObjectMutation.isPending &&
                    deleteObjectMutation.variables?.objectId === asset.relationId
                  }
                />
              ))
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Contacts section */}
        <Collapsible
          className="rounded-xl bg-white"
          isOpen={openSections.contacts}
          onOpenChange={
            // eslint-disable-next-line i18next/no-literal-string
            () => toggleSection('contacts')
          }>
          <CollapsibleTrigger className="w-full p-4">
            <SectionHeader
              title={tAdmin(($) => $.users.details.sections.contacts)}
              count={contacts.length}
              isOpen={openSections.contacts}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4">
            {contacts.length === 0 ? (
              <Body className="text-sm text-secondary">
                {tAdmin(($) => $.users.details.emptyStates.contacts)}
              </Body>
            ) : (
              contacts.map((contact) => (
                <ObjectRow
                  key={contact.id}
                  label={contact.name}
                  sublabel={contact.email || contact.phone || undefined}
                  onPress={() =>
                    setSelectedObject({
                      title: contact.name,
                      fields: [
                        {
                          label: tAdmin(($) => $.users.details.fields.id),
                          value: contact.id,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.email),
                          value: contact.email,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.phone),
                          value: contact.phone,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.created),
                          value: contact.createdAt,
                        },
                      ],
                    })
                  }
                  onDelete={() =>
                    // eslint-disable-next-line i18next/no-literal-string
                    confirmDelete('contact', contact.id, contact.name)
                  }
                  isDeleting={
                    deleteObjectMutation.isPending &&
                    deleteObjectMutation.variables?.objectId === contact.id
                  }
                />
              ))
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Contact requests section */}
        <Collapsible
          className="rounded-xl bg-white"
          isOpen={openSections.contactRequests}
          onOpenChange={
            // eslint-disable-next-line i18next/no-literal-string
            () => toggleSection('contactRequests')
          }>
          <CollapsibleTrigger className="w-full p-4">
            <SectionHeader
              title={tAdmin(($) => $.users.details.sections.contactRequests)}
              count={contactRequests.length}
              isOpen={openSections.contactRequests}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4">
            {contactRequests.length === 0 ? (
              <Body className="text-sm text-secondary">
                {tAdmin(($) => $.users.details.emptyStates.contactRequests)}
              </Body>
            ) : (
              contactRequests.map((cr) => (
                <ObjectRow
                  key={cr.id}
                  label={cr.assetName ?? tAdmin(($) => $.users.details.fields.assetUnknown)}
                  sublabel={cr.status}
                  onPress={() =>
                    setSelectedObject({
                      title: tAdmin(($) => $.users.details.sections.contactRequests),
                      fields: [
                        {
                          label: tAdmin(($) => $.users.details.fields.id),
                          value: cr.id,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.asset),
                          value: cr.assetName,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.status),
                          value: cr.status,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.created),
                          value: cr.createdAt,
                        },
                      ],
                    })
                  }
                  onDelete={() =>
                    confirmDelete(
                      // eslint-disable-next-line i18next/no-literal-string
                      'contactRequest',
                      cr.id,
                      cr.assetName ?? tAdmin(($) => $.users.details.sections.contactRequests)
                    )
                  }
                  isDeleting={
                    deleteObjectMutation.isPending &&
                    deleteObjectMutation.variables?.objectId === cr.id
                  }
                />
              ))
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Feedback section */}
        <Collapsible
          className="rounded-xl bg-white"
          isOpen={openSections.feedback}
          onOpenChange={
            // eslint-disable-next-line i18next/no-literal-string
            () => toggleSection('feedback')
          }>
          <CollapsibleTrigger className="w-full p-4">
            <SectionHeader
              title={tAdmin(($) => $.users.details.sections.feedback)}
              count={feedback.length}
              isOpen={openSections.feedback}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4">
            {feedback.length === 0 ? (
              <Body className="text-sm text-secondary">
                {tAdmin(($) => $.users.details.emptyStates.feedback)}
              </Body>
            ) : (
              feedback.map((f) => (
                <ObjectRow
                  key={f.id}
                  label={dayjs(f.lastSentAt).format('D.M.YYYY HH:mm')}
                  badge={<FeedbackStatusBadge status={f.status} />}
                  sublabel={
                    f.notificationCount > 0
                      ? // eslint-disable-next-line i18next/no-literal-string
                        `${f.notificationCount} msg${f.notificationCount > 1 ? 's' : ''}`
                      : undefined
                  }
                  onPress={() =>
                    setSelectedObject({
                      title: tAdmin(($) => $.users.details.sections.feedback),
                      fields: [
                        {
                          label: tAdmin(($) => $.users.details.fields.id),
                          value: f.id,
                        },
                        {
                          // eslint-disable-next-line i18next/no-literal-string
                          label: 'Sent',
                          value: f.lastSentAt ? dayjs(f.lastSentAt).format('D.M.YYYY HH:mm') : null,
                        },
                        {
                          // eslint-disable-next-line i18next/no-literal-string
                          label: 'Opened',
                          value: f.openedAt ? dayjs(f.openedAt).format('D.M.YYYY HH:mm') : 'No',
                        },
                        {
                          // eslint-disable-next-line i18next/no-literal-string
                          label: 'Declined at',
                          value: f.declinedAt ? dayjs(f.declinedAt).format('D.M.YYYY HH:mm') : null,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.status),
                          value: f.status,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.created),
                          value: f.createdAt,
                        },
                      ],
                    })
                  }
                  onDelete={() =>
                    confirmDelete(
                      // eslint-disable-next-line i18next/no-literal-string
                      'feedback',
                      f.id,
                      dayjs(f.lastSentAt).format('D.M.YYYY')
                    )
                  }
                  isDeleting={
                    deleteObjectMutation.isPending &&
                    deleteObjectMutation.variables?.objectId === f.id
                  }
                />
              ))
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Events section */}
        <Collapsible
          className="rounded-xl bg-white"
          isOpen={openSections.events}
          onOpenChange={() => {
            toggleSection('events')
          }}>
          <CollapsibleTrigger className="w-full p-4">
            <SectionHeader
              title={tAdmin(($) => $.users.details.sections.events)}
              count={events.length}
              isOpen={openSections.events}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4">
            {events.length === 0 ? (
              <Body className="text-sm text-secondary">
                {tAdmin(($) => $.users.details.emptyStates.events)}
              </Body>
            ) : (
              events.map((event) => (
                <ObjectRow
                  key={event.id}
                  label={event.title}
                  sublabel={
                    event.eventDate
                      ? new Date(event.eventDate).toLocaleDateString('fi-FI')
                      : event.eventType
                  }
                  onPress={() =>
                    setSelectedObject({
                      title: event.title,
                      fields: [
                        {
                          label: tAdmin(($) => $.users.details.fields.id),
                          value: event.id,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.type),
                          value: event.eventType,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.date),
                          value: event.eventDate
                            ? new Date(event.eventDate).toLocaleString('fi-FI')
                            : null,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.created),
                          value: event.createdAt,
                        },
                      ],
                    })
                  }
                  onDelete={() =>
                    // eslint-disable-next-line i18next/no-literal-string
                    confirmDelete('event', event.id, event.title)
                  }
                  isDeleting={
                    deleteObjectMutation.isPending &&
                    deleteObjectMutation.variables?.objectId === event.id
                  }
                />
              ))
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Interests section */}
        <Collapsible
          className="rounded-xl bg-white"
          isOpen={openSections.interests}
          onOpenChange={
            // eslint-disable-next-line i18next/no-literal-string
            () => toggleSection('interests')
          }>
          <CollapsibleTrigger className="w-full p-4">
            <SectionHeader
              title={tAdmin(($) => $.users.details.sections.interests)}
              count={interests.length}
              isOpen={openSections.interests}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4">
            {interests.length === 0 ? (
              <Body className="text-sm text-secondary">
                {tAdmin(($) => $.users.details.emptyStates.interests)}
              </Body>
            ) : (
              interests.map((interest) => {
                const data = parseJSONData(interest.data, propertyInterestDataSchema)
                // eslint-disable-next-line i18next/no-literal-string
                const name = `${data?.filters?.priceMax}€ - ${data?.locations
                  ?.map((location) => location.label)
                  .slice(0, 2)
                  .join(', ')}`

                return (
                  <ObjectRow
                    key={interest.id}
                    label={name}
                    sublabel={
                      interest.createdAt
                        ? dayjs(interest.createdAt).format('D.M.YYYY HH:mm')
                        : undefined
                    }
                    onPress={() =>
                      setSelectedObject({
                        title: tAdmin(($) => $.users.details.sections.interests),
                        fields: [
                          {
                            label: tAdmin(($) => $.users.details.fields.id),
                            value: interest.id,
                          },
                          {
                            label: tAdmin(($) => $.users.details.fields.description),
                            value: interest.description,
                          },
                          {
                            label: tAdmin(($) => $.users.details.fields.contact),
                            value: interest.contactName,
                          },
                          {
                            label: tAdmin(($) => $.users.details.fields.data),
                            value: JSON.stringify(interest.data, null, 2),
                          },
                          {
                            label: tAdmin(($) => $.users.details.fields.created),
                            value: interest.createdAt,
                          },
                        ],
                      })
                    }
                    onDelete={() =>
                      confirmDelete(
                        // eslint-disable-next-line i18next/no-literal-string
                        'interest',
                        interest.id,
                        interest.description ?? tAdmin(($) => $.users.details.sections.interests)
                      )
                    }
                    isDeleting={
                      deleteObjectMutation.isPending &&
                      deleteObjectMutation.variables?.objectId === interest.id
                    }
                  />
                )
              })
            )}
          </CollapsibleContent>
        </Collapsible>

        <SectionTitle
          textClassName="text-center text-base"
          className="mt-2 border-t border-neutral-200 pt-6">
          {tAdmin(($) => $.users.details.note)}
        </SectionTitle>

        {/* Feedback as responder section */}
        <Collapsible
          className="rounded-xl bg-white"
          isOpen={openSections.feedbackAsResponder}
          onOpenChange={
            // eslint-disable-next-line i18next/no-literal-string
            () => toggleSection('feedbackAsResponder')
          }>
          <CollapsibleTrigger className="w-full p-4">
            <SectionHeader
              title={tAdmin(($) => $.users.details.sections.feedbackAsResponder)}
              count={feedbackAsResponder.length}
              isOpen={openSections.feedbackAsResponder}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4">
            {feedbackAsResponder.length === 0 ? (
              <Body className="text-sm text-secondary">
                {tAdmin(($) => $.users.details.emptyStates.feedback)}
              </Body>
            ) : (
              feedbackAsResponder.map((f) => (
                <ReadOnlyRow
                  key={f.id}
                  label={dayjs(f.lastSentAt).format('D.M.YYYY HH:mm')}
                  badge={<FeedbackStatusBadge status={f.status} />}
                  onPress={() =>
                    setSelectedObject({
                      title: tAdmin(($) => $.users.details.sections.feedbackAsResponder),
                      fields: [
                        {
                          label: tAdmin(($) => $.users.details.fields.id),
                          value: f.id,
                        },
                        {
                          // eslint-disable-next-line i18next/no-literal-string
                          label: 'Sent',
                          value: f.lastSentAt ? dayjs(f.lastSentAt).format('D.M.YYYY HH:mm') : null,
                        },
                        {
                          // eslint-disable-next-line i18next/no-literal-string
                          label: 'Opened',
                          value: f.openedAt ? dayjs(f.openedAt).format('D.M.YYYY HH:mm') : 'No',
                        },
                        {
                          // eslint-disable-next-line i18next/no-literal-string
                          label: 'Declined at',
                          value: f.declinedAt ? dayjs(f.declinedAt).format('D.M.YYYY HH:mm') : null,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.status),
                          value: f.status,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.created),
                          value: f.createdAt,
                        },
                      ],
                    })
                  }
                />
              ))
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Matching contacts section (read-only) */}
        <Collapsible
          className="rounded-xl bg-white"
          isOpen={openSections.matchingContacts}
          onOpenChange={
            // eslint-disable-next-line i18next/no-literal-string
            () => toggleSection('matchingContacts')
          }>
          <CollapsibleTrigger className="w-full p-4">
            <SectionHeader
              title={tAdmin(($) => $.users.details.sections.matchingContacts)}
              count={matchingContacts.length}
              isOpen={openSections.matchingContacts}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4">
            {matchingContacts.length === 0 ? (
              <Body className="text-sm text-secondary">
                {tAdmin(($) => $.users.details.emptyStates.matchingContacts)}
              </Body>
            ) : (
              matchingContacts.map((contact) => (
                <ReadOnlyRow
                  key={contact.id}
                  label={contact.name}
                  sublabel={contact.email || contact.phone || undefined}
                  onPress={() =>
                    setSelectedObject({
                      title: contact.name,
                      fields: [
                        {
                          label: tAdmin(($) => $.users.details.fields.id),
                          value: contact.id,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.email),
                          value: contact.email,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.phone),
                          value: contact.phone,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.created),
                          value: contact.createdAt,
                        },
                        {
                          label: tAdmin(($) => $.users.details.fields.owner),
                          value: contact.userId,
                        },
                      ],
                    })
                  }
                />
              ))
            )}
          </CollapsibleContent>
        </Collapsible>
      </ContentBox>

      {/* Object detail modal */}
      <Dialog open={!!selectedObject} onOpenChange={(open) => !open && setSelectedObject(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedObject?.title}</DialogTitle>
          </DialogHeader>
          <ScrollView className="max-h-96">
            {selectedObject?.fields.map((field, index) => (
              <DetailRow key={index} label={field.label} value={field.value} />
            ))}
          </ScrollView>
        </DialogContent>
      </Dialog>

      {/* Slug edit dialog */}
      <Dialog
        open={editingSlug}
        onOpenChange={(open) => {
          if (!open) {
            setEditingSlug(false)
          }
        }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tAdmin(($) => $.users.details.editSlug.title)}</DialogTitle>
          </DialogHeader>
          <View className="gap-4">
            <Input
              value={slugInput}
              onChangeText={setSlugInput}
              placeholder={tAdmin(($) => $.users.details.editSlug.placeholder)}
              autoCapitalize="none"
              autoCorrect={false}
            />
            <Button
              onPress={() => updateSlugMutation.mutate(slugInput)}
              loading={updateSlugMutation.isPending}
              disabled={!slugInput.trim() || updateSlugMutation.isPending}>
              <ButtonLabel>{tAdmin(($) => $.users.details.editSlug.save)}</ButtonLabel>
            </Button>
          </View>
        </DialogContent>
      </Dialog>
    </ScreenView>
  )
}
